import { format, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Markdown from "react-markdown";
import { Link } from "react-router";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { MarketingLayout } from "~/app/components/landing/marketing-layout";
import { Button } from "~/app/components/ui/button";
import { t } from "~/app/i18n";
import { getDateFnsLocale, type LocaleHandle } from "~/app/lib/locale";
import * as Paths from "~/app/paths";
import { listChangelogEntries } from "~/domain/changelog";
import type { Route } from "./+types/changelog";

export const handle: LocaleHandle = { locale: "pl" };

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const requestedPage = Number(url.searchParams.get("page") ?? "1");

  return listChangelogEntries({
    page: Number.isFinite(requestedPage) ? requestedPage : 1,
    pageSize: 10,
  });
}

function formatPublishedAt(value: string) {
  const parsed = parseISO(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return format(parsed, "PPP", {
    locale: getDateFnsLocale(),
  });
}

export default function ChangelogPage({ loaderData }: Route.ComponentProps) {
  const { items, page, totalPages } = loaderData;

  return (
    <MarketingLayout locale="pl">
      <div className="mx-auto w-full max-w-4xl px-4 py-16 md:py-24">
        <div className="mb-10">
          <h1 className="text-4xl font-semibold tracking-tight text-slate-900 md:text-5xl">
            Changelog
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
            Najnowsze zmiany w AnyLawyer, nowe funkcje i usprawnienia zebrane w
            jednym miejscu.
          </p>
        </div>

        <div className="divide-y divide-slate-200">
          {items.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
              {t("Changelog.emptyState")}
            </div>
          ) : (
            items.map((item) => (
              <article
                key={item.slug}
                id={item.slug}
                className="scroll-mt-28 py-8 first:pt-0 last:pb-0"
              >
                <p className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
                  {formatPublishedAt(item.publishedAt)}
                </p>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                  {item.title}
                </h2>
                <div className="prose prose-slate mt-4 max-w-none prose-h1:hidden prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-slate-900 prose-p:text-slate-600 prose-p:leading-7 prose-li:text-slate-600 prose-li:leading-7 prose-a:text-brand-600 prose-a:no-underline hover:prose-a:text-brand-700 prose-h2:mt-10 prose-h2:text-lg prose-h2:md:text-xl prose-h3:text-base prose-h3:md:text-lg">
                  <Markdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  >
                    {item.content}
                  </Markdown>
                </div>
              </article>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-between gap-4">
            <Button asChild variant="outline" disabled={page <= 1}>
              <Link to={Paths.changelog(page - 1)} aria-disabled={page <= 1}>
                <ChevronLeft className="mr-1 h-4 w-4" />
                {t("Previous")}
              </Link>
            </Button>

            <p className="text-sm text-slate-500">
              {t("Page")} {page} {t("of")} {totalPages}
            </p>

            <Button asChild variant="outline" disabled={page >= totalPages}>
              <Link
                to={Paths.changelog(page + 1)}
                aria-disabled={page >= totalPages}
              >
                {t("Next")}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </MarketingLayout>
  );
}
